import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useDashboardFolderActions } from 'redux/actions/DashboardFolders'
import { useDashboardActions } from 'redux/actions/Dashboards'
import { useDashboardGroupActions } from 'redux/actions/DashboardGroups'
import { useReportActions } from 'redux/actions/Reports'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useAvailableDashboardFolders,
  useDashboardFolder,
  useDashboardFoldersFetched
} from 'redux/hooks/DashboardFolders'
import {
  useAvailableDashboardGroupsIncludeDashboards,
  useDashboardGroupsLoading,
  useDeletedDashboardGroups,
  useDeletedDashboardGroupsLoading
} from 'redux/hooks/DashboardGroups'

import { BreadcrumbsProps } from 'components_new/molecules/Breadcrumbs'
import { Context } from 'types/GlobalUserEvents'
import { TResource } from 'types/GlobalResource'

import { useIsMobile, useParams, useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'
import TextField from 'components_new/atoms/TextField'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'
import Text from 'components_new/atoms/Text'

import EditResourceMenu from 'components_new/organisms/EditResourceMenu'
import FolderOptionsMenu from 'components_new/organisms/FolderOptionsMenu'
import ResourcesTable from 'components_new/organisms/ResourcesTable'
import { EResourcesTableTab } from 'components_new/organisms/ResourcesTable/utils'

import PageLayout from 'components_new/templates/PageLayout'

import { addRef, useQueryParamState } from 'utils/queryHandler'
import { useReports } from 'redux/hooks/Reports'

const Resources = () => {
  const { id: currentFolderId } = useParams<{ id: string }>()

  const folder = useDashboardFolder(currentFolderId)
  const history = useHistory()
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const breadcrumbs: BreadcrumbsProps['items'] = [
    {
      href: '/resources',
      icon: { name: 'Resources' },
      title: t('global', 'resources')
    },
    ...(folder
      ? [
          {
            href: `/resources/folders/${folder.id}`,
            icon: { name: 'Folder' as const },
            title: folder.name
          }
        ]
      : [])
  ]

  const [tab] = useQueryParamState(
    'tab',
    null,
    Object.values(EResourcesTableTab)
  )
  const isTrash = tab === EResourcesTableTab.TRASH
  // Only the "Alla" tab (tab === null) uses the folder structure. Every named
  // tab (Publicerad/Utkast/Favoriter/Papperskorgen) is shown flat, listing all
  // matching resources regardless of which folder they live in.
  const isFlat = tab !== null

  const dashboardGroups = useAvailableDashboardGroupsIncludeDashboards()
  const deletedDashboardGroups = useDeletedDashboardGroups()
  const deletedLoading = useDeletedDashboardGroupsLoading()
  const reports = useReports()
  const folders = useAvailableDashboardFolders()
  const {
    allowAccess,
    allowCreateDashboard,
    allowCreateReport,
    dashboardLimitReached,
    isAdmin,
    isCompanyGroup,
    loading: accountSettingsLoading,
    numberOfDashboardsInLicense,
    numberOfDashboardsInUse
  } = useAccountSettings()

  const foldersFetched = useDashboardFoldersFetched()
  const isLoadingDashboards = useDashboardGroupsLoading()

  /*-- actions --*/
  const { tryCreateDashboard } = useDashboardActions()
  const { tryCreateDashboardFolder } = useDashboardFolderActions()
  const { tryCreateReport } = useReportActions()
  const { tryGetDeletedDashboardGroups, tryRestoreDashboardGroup } =
    useDashboardGroupActions()

  // Lazily fetch the trash the first time the user opens the trash tab.
  useEffect(() => {
    if (isTrash) {
      tryGetDeletedDashboardGroups()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrash])

  // A named tab is a flat, top-level view and cannot live inside a folder, so
  // drop the folder from the URL (keeping the tab) if we end up in that state
  // (direct link, or selecting a tab while inside a folder).
  useEffect(() => {
    if (currentFolderId && tab) {
      history.replace(`/resources?tab=${tab}`)
    }
  }, [currentFolderId, tab, history])

  /*-- states --*/
  const [dialog, setDialog] = useState<{
    open: boolean
    value: string
    variant: 'add' | 'add_folder'
  }>({
    open: false,
    value: '',
    variant: 'add'
  })

  const [menu, setMenu] = useState<{
    id: string | null
    menuAnchorEl: HTMLAnchorElement | null
    variant: TResource['variant']
  }>({
    id: null,
    menuAnchorEl: null,
    variant: 'dashboard'
  })

  const [restoreResource, setRestoreResource] = useState<TResource | null>(null)

  /*-- handlers --*/
  const handleClickRestore = (resource: TResource) => {
    setRestoreResource(resource)
  }

  const handleCloseRestore = () => {
    setRestoreResource(null)
  }

  const handleConfirmRestore = () => {
    if (restoreResource) {
      tryRestoreDashboardGroup(restoreResource.id)
    }

    setRestoreResource(null)
  }

  const handleClickAdd = () => {
    setDialog((prevState) => ({ ...prevState, open: true, variant: 'add' }))
  }

  const handleClickAddFolder = () => {
    setDialog((prevState) => ({
      ...prevState,
      open: true,
      variant: 'add_folder'
    }))
  }

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false, value: '' }))
  }

  const handleCloseMenu = () => {
    setMenu((prevState) => ({
      ...prevState,
      menuAnchorEl: null
    }))
  }

  const handleCreateDashboard = () => {
    tryCreateDashboard(
      {
        data: {
          folder_id: currentFolderId
        }
      },
      (id) => {
        history.push(
          addRef(`/dashboards/${id}?new_dashboard=true`, Context.FROM_NAVBAR)
        )
      },
      Context.FROM_SCRATCH
    )
  }

  const handleCreateReport = () => {
    tryCreateReport(
      {
        folder_id: currentFolderId
      },
      (id) => {
        history.push(`/reports/${id}?edit=true`)
      }
    )
  }

  const handleCreateFolder = () => {
    tryCreateDashboardFolder({
      data: { name: dialog.value }
    })

    handleCloseDialog()
  }

  const handleOpenOptionsMenu = (event: any, resource: TResource) => {
    setMenu({
      id: resource.id,
      menuAnchorEl: event.currentTarget,
      variant: resource.variant
    })
  }

  // ---
  const loading =
    isLoadingDashboards ||
    !foldersFetched ||
    accountSettingsLoading ||
    (isTrash && deletedLoading)

  const resources = useResources({ dashboardGroups, reports })
  const deletedResources = useResources({
    dashboardGroups: deletedDashboardGroups
  })

  const items = isTrash
    ? deletedResources
    : isFlat
      ? resources
      : resources.filter((resource) =>
        currentFolderId
          ? resource.folderId === currentFolderId
          : !resource.folderId
      )

  const createFolderDisabled = location.pathname.includes('folders')

  let currentUseOfLicense = undefined

  if (numberOfDashboardsInLicense) {
    currentUseOfLicense = `${numberOfDashboardsInUse} ${t(
      'global',
      'of',
      true
    )} ${numberOfDashboardsInLicense}`
  }

  return (
    <>
      <PageLayout
        footer={{
          mobile: {
            showNavigation: true
          }
        }}
        topbar={{
          actions:
            !isMobile && allowCreateDashboard ? (
              <Button
                onClick={handleClickAdd}
                startIcon={<Icon name="Add" />}
                variant="text"
              >
                {t('global', 'add')}
              </Button>
            ) : undefined,
          breadcrumbs: breadcrumbs,
          mobile: {
            goBack: Boolean(folder)
          }
        }}
        variant="static"
      >
        <ResourcesTable
          context={Context.FROM_DASHBOARDS_PAGE}
          editMode={!isCompanyGroup && !isMobile}
          filters={{
            favorite: true,
            status: true,
            trash: true,
            variant: true
          }}
          folders={currentFolderId || isFlat ? [] : folders}
          loading={loading}
          items={items}
          onClickAdd={handleClickAdd}
          onClickItemOptionsMenu={handleOpenOptionsMenu}
          onClickRestore={handleClickRestore}
          sx={{ flex: 1 }}
        />
      </PageLayout>

      {/*-- menus --*/}

      {/*-- edit dashboard --*/}
      {menu.id ? (
        <EditResourceMenu
          id={menu.id}
          menuAnchorEl={menu.variant !== 'folder' ? menu.menuAnchorEl : null}
          onClose={handleCloseMenu}
          variant={menu.variant}
        />
      ) : null}

      {/*-- edit dashboard --*/}
      {menu.id ? (
        <FolderOptionsMenu
          folderId={menu.id}
          handleClose={handleCloseMenu}
          menuAnchorEl={menu.variant === 'folder' ? menu.menuAnchorEl : null}
        />
      ) : null}

      {/*-- dialogs --*/}

      {/*-- add --*/}
      <SimpleDialog
        actions={
          dialog.variant === 'add_folder' ? (
            <>
              <Button onClick={handleCloseDialog} variant="text">
                {t('global', 'cancel')}
              </Button>
              <Button onClick={handleCreateFolder}>
                {t('global', 'create')}
              </Button>
            </>
          ) : null
        }
        onClose={handleCloseDialog}
        open={
          dialog.open &&
          (dialog.variant === 'add' || dialog.variant === 'add_folder')
        }
        title={
          dialog.variant === 'add_folder'
            ? t('dashboards', 'create_folder')
            : undefined
        }
      >
        {dialog.variant === 'add' ? (
          <>
            <GenericListItem
              action="to"
              body={currentUseOfLicense}
              disabled={dashboardLimitReached}
              onClick={handleCreateDashboard}
              title={`${t('global', 'add')} ${t('global', 'dashboard', true)}`}
              variant="dashboard"
            />

            {allowAccess.allow_reports ? (
              <GenericListItem
                action="to"
                disabled={!allowCreateReport}
                onClick={handleCreateReport}
                title={`${t('global', 'add')} ${t('global', 'report', true)}`}
                variant="report"
              />
            ) : null}

            <GenericListItem
              action="add"
              disabled={createFolderDisabled || !isAdmin}
              onClick={handleClickAddFolder}
              title={`${t('global', 'add')} ${t('global', 'folder', true)}`}
              variant="folder"
            />
          </>
        ) : (
          <TextField
            autoFocus={true}
            label={t('global', 'name')}
            required={true}
            autoComplete="off"
            value={dialog.value}
            onChange={(event) => {
              setDialog((prevState) => ({
                ...prevState,
                value: event.target.value
              }))
            }}
          />
        )}
      </SimpleDialog>

      {/*-- restore --*/}
      <SimpleDialog
        actions={
          <>
            <Button onClick={handleCloseRestore} variant="text">
              {t('global', 'cancel')}
            </Button>
            <Button onClick={handleConfirmRestore}>
              {t('global', 'restore')}
            </Button>
          </>
        }
        onClose={handleCloseRestore}
        open={Boolean(restoreResource)}
        title={t('resources', 'restore_dashboard_title')}
      >
        <Text>{t('resources', 'restore_dashboard_body')}</Text>
      </SimpleDialog>
    </>
  )
}

export default Resources
